import { Link, useNavigate } from 'react-router-dom';
import { Star, Heart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useRatings } from '../context/RatingsContext';
import { useWishlist } from '../context/WishlistContext';
import { formatFCFA } from '../lib/currency';

const SKIN_TYPE_FR = { 'Oily': 'Grasse', 'Dry': 'S\u00e8che', 'Sensitive': 'Sensible', 'All': 'Tous' };
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

// Resolve possibly-relative image URL to absolute
const resolveImage = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/')) return `${BACKEND}${url}`;
  return url;
};

export default function ProductCard({ product }) {
  const { addToCart } = useCart();
  const { ratings } = useRatings();
  const { toggle, has } = useWishlist();
  const navigate = useNavigate();
  const rating = ratings[product.product_id];
  const inWishlist = has(product.product_id);

  const handleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const res = await toggle(product.product_id);
    if (res?.requireAuth) navigate('/login');
  };

  // Value signals
  const originalPrice = product.original_price && product.original_price > product.price ? product.original_price : null;
  const savings = originalPrice ? originalPrice - product.price : 0;
  const lowStock = typeof product.stock === 'number' && product.stock > 0 && product.stock < 10;

  return (
    <div data-testid={`product-card-${product.product_id}`} className="product-card group relative">
      <Link to={`/product/${product.product_id}`} className="block">
        <div className="aspect-square bg-[#EDE8DE] overflow-hidden mb-4 relative">
          <img
            src={resolveImage(product.image_url)}
            alt={product.name}
            className="product-image w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            loading="lazy"
          />
          {product.best_seller && (
            <div className="absolute top-3 left-3 text-[9px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 z-10 shadow-md" style={{ backgroundColor: '#B80000', color: '#fff' }}>
              Best-Seller
            </div>
          )}
          {lowStock && !product.best_seller && (
            <div data-testid={`lowstock-${product.product_id}`} className="absolute top-3 left-3 text-[9px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 z-10 shadow-md" style={{ backgroundColor: '#0A0A0A', color: '#fff' }}>
              Plus que {product.stock}
            </div>
          )}
          <button
            data-testid={`wishlist-toggle-${product.product_id}`}
            onClick={handleWishlist}
            aria-label={inWishlist ? 'Retirer des favoris' : 'Ajouter aux favoris'}
            className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center bg-[#F5F0E8]/90 backdrop-blur-sm hover:bg-[#F5F0E8] transition-all"
          >
            <Heart
              size={16}
              fill={inWishlist ? '#B80000' : 'none'}
              stroke={inWishlist ? '#B80000' : '#000'}
              strokeWidth={1.5}
            />
          </button>
          <div className="product-card-overlay absolute bottom-0 left-0 right-0 p-4 bg-[#F5F0E8]/95 backdrop-blur-sm">
            <button
              data-testid={`add-to-cart-${product.product_id}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                addToCart(product.product_id);
              }}
              className="w-full btn-primary text-center"
            >
              Ajouter au Panier
            </button>
          </div>
        </div>
      </Link>

      <div className="space-y-1">
        <p className="text-xs uppercase tracking-[0.15em] text-black/40 font-semibold">Peau {SKIN_TYPE_FR[product.skin_type] || product.skin_type}</p>
        <h3 className="font-serif text-lg font-normal text-black" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          {product.name}
        </h3>
        <div className="flex items-center justify-between pt-0.5">
          <div className="flex items-baseline gap-2">
            {originalPrice && (
              <span className="text-xs text-black/35 line-through">{formatFCFA(originalPrice)}</span>
            )}
            <span className="text-sm font-semibold text-black">{formatFCFA(product.price)}</span>
          </div>
          {rating ? (
            <div className="flex items-center gap-1" data-testid={`product-rating-${product.product_id}`}>
              <Star size={11} fill="#B80000" stroke="#B80000" strokeWidth={1.5} />
              <span className="text-[11px] text-black/60 font-medium">{Number(rating.avg).toFixed(1)}</span>
              <span className="text-[10px] text-black/30">· {rating.count} avis</span>
            </div>
          ) : null}
        </div>
        {savings > 0 && (
          <p className="text-[10px] uppercase tracking-[0.15em] font-bold" style={{ color: '#B80000' }}>
            Économisez {formatFCFA(savings)}
          </p>
        )}
      </div>
    </div>
  );
}
